import React, { useEffect, useState } from 'react';
import {
  TextInput,
  Button,
  Box,
  Group,
  Select,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import toast from 'react-hot-toast';

import ApiLinks from '../../Navlinks/ApiLinks';
import LoaderOverlay from '../../Utilities/LoadingOverLay';

function AddCity() {
  const location = useLocation();
  const id = location.state?.id;
  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [stateOptions, setStateOptions] = useState([]);

  // Configure the form with an empty string for stateid
  const form = useForm({
    initialValues: {
      cityid: 0,
      stateid: '',
      compcode: user?.compcode || '',
      cityname: '',
    },
  });

  // Fetch state options from the API
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/${ApiLinks.State_list}/${user.compcode}`)
      .then((response) => {
        if (response.data.status) {
          const options = response.data.data.map((state) => ({
            value: state.stateid.toString(), // ensure value is a string
            label: state.statename,
          }));
          setStateOptions(options);
        }
      })
      .catch((error) => {
        console.error('Error fetching states:', error);
      });
  }, [user.compcode]);

  // If editing, fetch the city details and prepopulate the form (including stateid)
  useEffect(() => {
    if (id) {
      setLoading(true);
      axios
        .get(
          `${process.env.REACT_APP_API_URL}/${ApiLinks.City_GetById}/${id}/${user.compcode}`
        )
        .then((response) => {
          if (response.data.status && response.data.data.length > 0) {
            const data = response.data.data[0];
            form.setValues({
              cityid: data.cityid,
              compcode: data.compcode,
              stateid: data.stateid ? data.stateid.toString() : '',
              cityname: data.cityname,
            });
          }
        })
        .catch((err) => {
          console.error('Error loading city:', err);
        })
        .finally(() => setLoading(false));
    }
  }, [id, user.compcode]);

  const handleSubmit = (values) => {
    if (!values.cityname.trim()) {
      toast.error('City name is required.');
      return;
    }

    // Convert stateid to a number if your API requires it
    const payload = { ...values, stateid: parseInt(values.stateid, 10) };

    setLoading(true);

    // Use PUT if editing, POST if creating a new city
    const request = id && id !== 0
      ? axios.put(`${process.env.REACT_APP_API_URL}/${ApiLinks.City_Update}`, payload, {
        headers: { 'Content-Type': 'application/json' },
      })
      : axios.post(`${process.env.REACT_APP_API_URL}/${ApiLinks.City_Save}`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });

    request
      .then((res) => {
        if (res.data.status) {
          Swal.fire({
            title: id ? 'Updated!' : 'Created!',
            text: `City ${id ? 'updated' : 'created'} successfully.`,
            icon: 'success',
            confirmButtonText: 'OK',
          }).then(() => {
            navigate('/dashboard/city');
          });
        } else {
          Swal.fire('Error!', res.data.message || 'Failed to save city.', 'error');
        }
      })
      .catch(() => {
        Swal.fire('Error!', 'An error occurred while saving.', 'error');
      })
      .finally(() => setLoading(false));
  };

  if (loading) return <LoaderOverlay />;

  return (
    <Box
      maw={500}
      mx="auto"
      sx={(theme) => ({
        backgroundColor: 'white',
        padding: theme.spacing.xl,
        borderRadius: theme.radius.md,
        boxShadow: theme.shadows.md,
      })}
    >
      <Group position="apart" mb="md">
        <h2>{id ? 'Edit City' : 'Create City'}</h2>
        <Button onClick={() => navigate('/dashboard/city')}>Back</Button>
      </Group>



      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Select
          label="State"
          placeholder="Select State"
          data={stateOptions}
          value={form.values.stateid}
          onChange={(value) => form.setFieldValue('stateid', value)}
          required
          searchable
          nothingFound="No options"
          mb="xl"
        />

        <TextInput
          label="City Name"
          placeholder="Enter city name"
          {...form.getInputProps('cityname')}
        />

        <Button type="submit" mt="lg" fullWidth>
          {id ? 'Update' : 'Create'}
        </Button>
      </form>
    </Box>
  );
}

export default AddCity;
